<script lang="ts">
  // One virtualised list row (spec §9 "Row" + components doc §4). Absolutely positioned by the
  // parent through `top`; owns its swipe gesture, drag source and hover actions. Reads the store
  // for selection / search state so the parent needs no per-row effects.
  import { formatDate, getLang, t } from '$lib/i18n';
  import { truncateAttachmentName } from '$lib/mail/format';
  import { labelsOf } from '$lib/mail/labels';
  import { isSnoozedFolder, snoozeChipText, snoozeUntil } from '$lib/mail/snooze';
  import { highlightSegments } from '$lib/search/highlight';
  import { store } from '$lib/store';
  import type { ThreadRow } from '$lib/types';
  import { canArchiveIn, deleteUids, rowUids, toggleStar } from '../shell/_local';
  import Avatar from '../ui/Avatar.svelte';
  import Icon from '../ui/Icon.svelte';

  let {
    row,
    index,
    top,
    cursor = false,
    finePointer = false,
    mobile = false,
    oncontext,
  }: {
    row: ThreadRow;
    index: number;
    top: number;
    cursor?: boolean;
    finePointer?: boolean;
    mobile?: boolean;
    oncontext?: (ev: MouseEvent, row: ThreadRow) => void;
  } = $props();

  const SWIPE_MAX = 120;
  const SWIPE_TRIGGER = 72;
  const SWIPE_CLICK_SUPPRESS = 8;

  let dx = $state(0);
  let swiping = $state(false);
  let dragging = $state(false);
  let suppressClick = false;
  let sx = 0;
  let sy = 0;
  let cancelled = false;

  const uids = $derived(rowUids(row));
  const selAll = $derived(uids.every((u) => store.selection.has(u)));
  const selSome = $derived(!selAll && uids.some((u) => store.selection.has(u)));
  const checked = $derived<'true' | 'false' | 'mixed'>(selAll ? 'true' : selSome ? 'mixed' : 'false');
  const unread = $derived(!row.flags.includes('\\Seen'));
  const flagged = $derived(row.flags.includes('\\Flagged'));
  const terms = $derived(store.searchTerms);
  const fromText = $derived(row.fromName || row.fromAddr || '');
  const fromSeg = $derived(highlightSegments(fromText, terms));
  const subjSeg = $derived(highlightSegments(row.subject || t('noSubject'), terms));
  const snipSeg = $derived(row.snippet ? highlightSegments(row.snippet, terms) : null);
  const labels = $derived(labelsOf(row.flags));
  const atts = $derived(row.atts.slice(0, 2));
  const attsMore = $derived(Math.max(0, row.atts.length - 2));
  const otherFolder = $derived(!!store.searchResults && row.folder !== store.currentFolder);
  const snoozeAt = $derived(isSnoozedFolder(row.folder) ? snoozeUntil(row.flags) : null);
  const canArchive = $derived(canArchiveIn(row.folder));
  const rtl = $derived(getLang() === 'ar');

  function hash(): string {
    return store.hashFor({ name: 'read', folder: row.folder, uid: row.uid });
  }

  function openNewTab() {
    window.open(location.pathname + hash(), '_blank', 'noopener');
  }

  function onClick(ev: MouseEvent) {
    if (suppressClick) return;
    if (ev.ctrlKey || ev.metaKey) {
      ev.preventDefault();
      openNewTab();
      return;
    }
    if (store.selection.size > 0) store.toggleRow(row);
    else void store.openMessage(row.uid, row.folder);
  }

  function onAux(ev: MouseEvent) {
    if (ev.button === 1) {
      ev.preventDefault();
      openNewTab();
    }
  }

  function onAvatar(ev: MouseEvent) {
    ev.stopPropagation();
    if (ev.shiftKey) store.selectRangeTo(row);
    else {
      store.toggleRow(row);
      store.lastAnchorKey = row.key;
    }
  }

  function onKey(ev: KeyboardEvent) {
    if (ev.key === 'Enter' && ev.target === ev.currentTarget) {
      store.cursorKey = row.key;
    }
  }

  function onContext(ev: MouseEvent) {
    if (!finePointer || !oncontext) return;
    ev.preventDefault();
    oncontext(ev, row);
  }

  function onDragStart(ev: DragEvent) {
    const list = store.selection.has(row.uid) ? [...store.selection] : uids;
    ev.dataTransfer?.setData('application/x-tik-uids', JSON.stringify(list));
    ev.dataTransfer?.setData('text/plain', row.subject || '');
    if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
    store.dragUids = list;
    store.dragFrom = row.folder;
    dragging = true;
  }

  function onDragEnd() {
    dragging = false;
    store.dragUids = null;
    store.dragFrom = null;
  }

  // ---- swipe (mobile only)
  function onTouchStart(ev: TouchEvent) {
    if (!mobile || ev.touches.length !== 1) return;
    if ((ev.target as Element | null)?.closest?.('.rowact')) return;
    sx = ev.touches[0]!.clientX;
    sy = ev.touches[0]!.clientY;
    cancelled = false;
    swiping = true;
    dx = 0;
  }
  function onTouchMove(ev: TouchEvent) {
    if (!swiping || cancelled) return;
    const t0 = ev.touches[0]!;
    const ddx = t0.clientX - sx;
    const ddy = t0.clientY - sy;
    if (Math.abs(ddy) > 30 && Math.abs(ddy) > Math.abs(ddx)) {
      cancelled = true;
      dx = 0;
      swiping = false;
      return;
    }
    dx = Math.max(-SWIPE_MAX, Math.min(SWIPE_MAX, ddx));
  }
  function onTouchEnd() {
    if (!swiping) return;
    const d = dx;
    swiping = false;
    dx = 0;
    if (Math.abs(d) >= SWIPE_CLICK_SUPPRESS) {
      suppressClick = true;
      setTimeout(() => (suppressClick = false), 300);
    }
    const del = rtl ? d > SWIPE_TRIGGER : d < -SWIPE_TRIGGER;
    const star = rtl ? d < -SWIPE_TRIGGER : d > SWIPE_TRIGGER;
    if (del) void deleteUids(uids, row.folder);
    else if (star) void toggleStar(uids, row.folder);
  }
  function onTouchCancel() {
    swiping = false;
    dx = 0;
  }

  // ---- hover actions
  function markToggle(ev: MouseEvent) {
    ev.stopPropagation();
    void store.act(unread ? 'seen' : 'unseen', row.folder, uids, undefined, { undoable: true });
  }
  function archive(ev: MouseEvent) {
    ev.stopPropagation();
    void store.archive(uids, row.folder);
  }
  function snooze(ev: MouseEvent) {
    ev.stopPropagation();
    store.snoozeTarget = uids;
  }
  function del(ev: MouseEvent) {
    ev.stopPropagation();
    void deleteUids(uids, row.folder);
  }
  function star(ev: MouseEvent) {
    ev.stopPropagation();
    void toggleStar(uids, row.folder);
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions a11y_click_events_have_key_events a11y_no_noninteractive_tabindex -->
<div
  class="row"
  class:unread
  class:cursor
  class:snap={!swiping}
  class:dragging
  role="row"
  tabindex="-1"
  draggable="true"
  id="r-{row.uid}"
  aria-rowindex={index + 1}
  aria-selected={selAll}
  data-uids={uids.join(',')}
  data-thread={row.threadId}
  style="transform:translateY({top}px) translateX({dx}px)"
  onclick={onClick}
  onauxclick={onAux}
  onkeydown={onKey}
  oncontextmenu={onContext}
  ondragstart={onDragStart}
  ondragend={onDragEnd}
  ontouchstart={onTouchStart}
  ontouchmove={onTouchMove}
  ontouchend={onTouchEnd}
  ontouchcancel={onTouchCancel}
>
  <Avatar name={row.fromName} addr={row.fromAddr} sel={selAll} {checked} onclick={onAvatar} />
  {#if finePointer}
    <button type="button" class="rstar" class:on={flagged} aria-pressed={flagged} aria-label={flagged ? t('unstar') : t('star')} onclick={star}><Icon name="star" size={15} /></button>
  {/if}
  <span class="mid" role="gridcell">
    <span class="from">
      <bdi>{#each fromSeg as s, i (i)}{#if s.hit}<mark>{s.s}</mark>{:else}{s.s}{/if}{/each}</bdi>
      {#if row.tcount}<span class="tbadge" role="note" aria-label={t('msgsN', { n: row.tcount })}>{row.tcount}</span>{/if}
    </span>
    <span class="subject">
      {#if otherFolder}<span class="fbadge"><bdi>{row.folder}</bdi></span>{/if}
      <bdi>{#each subjSeg as s, i (i)}{#if s.hit}<mark>{s.s}</mark>{:else}{s.s}{/if}{/each}</bdi>
      {#each labels as l (l)}<span class="lchip"><bdi>{l}</bdi></span>{/each}
      {#each atts as a (a)}<span class="atchip"><Icon name="clip" size={11} /><bdi>{truncateAttachmentName(a, 20)}</bdi></span>{/each}
      {#if attsMore > 0}<span class="atchip more">+{attsMore}</span>{/if}
      {#if snoozeAt}<span class="szchip">{snoozeChipText(snoozeAt, getLang())}</span>{/if}
      {#if snipSeg}<span class="snip">, <bdi>{#each snipSeg as s, i (i)}{#if s.hit}<mark>{s.s}</mark>{:else}{s.s}{/if}{/each}</bdi></span>{/if}
    </span>
  </span>
  <span class="side" role="gridcell">
    <time datetime={row.date}>{formatDate(row.date)}</time>
    <span class="marks">
      {#if !finePointer && flagged}<span class="star"><Icon name="star" size={13} /></span>{/if}
      {#if row.hasAttachments}<span class="clip"><Icon name="clip" size={13} /></span>{/if}
      {#if unread}<span class="dot-unread" aria-label={t('unreadA11y')}></span>{/if}
    </span>
  </span>
  <span class="rowact" role="gridcell">
    <button type="button" class="ra" aria-label={t('markUnread')} onclick={markToggle}><Icon name={unread ? 'check' : 'inbox'} size={16} /></button>
    {#if canArchive}<button type="button" class="ra" aria-label="{t('archive')} (E)" onclick={archive}><Icon name="archive" size={16} /></button>{/if}
    <button type="button" class="ra" aria-label={t('snooze')} onclick={snooze}><Icon name="moon" size={16} /></button>
    <button type="button" class="ra danger-h" aria-label="{t('del')} (#)" onclick={del}><Icon name="trash" size={16} /></button>
  </span>
</div>

<style>
  /* @css list/Row start */
  .row {
    position: absolute;
    inset-inline: 0;
    height: var(--row-h, 76px);
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding-inline: var(--space-4);
    border-bottom: 1px solid var(--border-subtle);
    text-align: start;
    width: 100%;
    content-visibility: auto;
    contain-intrinsic-size: auto var(--row-h, 76px);
    transition: background var(--duration-instant) var(--ease-standard);
    touch-action: pan-y;
    will-change: transform;
    cursor: pointer;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent
  }

  .row:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: -2px
  }

  .rowact {
    display: none
  }

  @media (hover:hover) and (pointer:fine) {
  .rowact {
      display: flex;
      align-items: center;
      gap: var(--space-1);
      visibility: hidden;
      flex: none
    }
  }

  @media (hover:hover) and (pointer:fine) {
  .side,
  .rowact {
      min-inline-size: calc(var(--control-sm) * 4 + var(--space-1) * 3)
    }
  }

  @media (hover:hover) and (pointer:fine) {
  .row:hover .rowact,
  .row:focus-within .rowact {
      visibility: visible
    }
  }

  @media (hover:hover) and (pointer:fine) {
  .row:hover .side,
  .row:focus-within .side {
      display: none
    }
  }

  .ra {
    display: grid;
    place-items: center;
    width: var(--control-sm);
    height: var(--control-sm);
    border-radius: var(--radius-control);
    color: var(--text-muted)
  }

  .ra:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .ra:active {
    background: var(--mail-pressed);
    color: var(--text-strong)
  }

  .ra.danger-h:hover {
    color: var(--danger-500)
  }

  .row.dragging {
    opacity: .45
  }

  .row.snap {
    transition: background var(--duration-instant) var(--ease-standard), transform .18s var(--ease-out)
  }

  .row:hover {
    background: var(--surface-subtle)
  }

  .row.cursor {
    outline: 2px solid var(--blue-500);
    outline-offset: -2px;
    background: var(--surface-subtle)
  }

  .atchip {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    margin-inline-start: var(--space-2);
    padding: 0 var(--space-2);
    border-radius: var(--radius-sm);
    background: var(--surface-sunken);
    border: 1px solid var(--border-default);
    color: var(--text-muted);
    font-size: var(--text-xs);
    font-weight: var(--weight-regular);
    vertical-align: middle;
    max-width: 15ch;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis
  }

  .atchip > bdi {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis
  }

  .atchip.more {
    color: var(--text-faint)
  }

  .lchip {
    display: inline-block;
    margin-inline-start: var(--space-2);
    padding: 0 var(--space-2);
    border-radius: var(--radius-sm);
    background: var(--info-100, var(--surface-sunken));
    border: 1px solid var(--border-accent);
    color: var(--text-link);
    font-size: var(--text-xs);
    font-weight: var(--weight-regular);
    vertical-align: middle
  }

  .szchip {
    display: inline-block;
    margin-inline-start: var(--space-2);
    padding: 0 var(--space-2);
    border-radius: var(--radius-sm);
    background: var(--surface-sunken);
    border: 1px dashed var(--border-default);
    color: var(--text-muted);
    font-size: var(--text-xs);
    vertical-align: middle
  }

  .fbadge {
    display: inline-block;
    margin-inline-end: var(--space-2);
    padding: 0 var(--space-2);
    border-radius: var(--radius-sm);
    background: var(--surface-sunken);
    border: 1px solid var(--border-default);
    color: var(--text-muted);
    font-size: var(--text-xs);
    font-weight: var(--weight-regular);
    vertical-align: middle
  }

  .row:has(:global(.avatar.sel)) {
    background: var(--blue-50)
  }

  :global(html.tik-dark) .row:has(:global(.avatar.sel)) {
    background: #4a7cf624
  }

  .row:active {
    background: var(--mail-pressed)
  }

  .mid {
    flex: 1;
    min-width: 0;
    display: grid;
    gap: var(--space-1)
  }

  .from {
    color: var(--text-strong);
    font-size: var(--text-sm);
    display: flex;
    align-items: center;
    gap: var(--space-2);
    text-align: start
  }

  .from > bdi {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis
  }

  .snip {
    color: var(--text-faint);
    font-weight: var(--weight-regular)
  }

  :global(.list[data-density=compact]) .snip {
    display: none
  }

  .subject {
    color: var(--text-muted);
    font-size: var(--text-sm);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: start
  }

  .unread .from,
  .unread .subject {
    font-weight: var(--weight-semibold);
    color: var(--text-strong)
  }

  .side {
    display: grid;
    justify-items: end;
    gap: 4px;
    flex: none
  }

  time {
    font-size: var(--text-xs);
    color: var(--text-faint);
    font-variant-numeric: tabular-nums
  }

  .unread time {
    color: var(--text-link);
    font-weight: var(--weight-semibold)
  }

  .marks {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-height: 16px
  }

  .tbadge {
    display: inline-grid;
    place-items: center;
    background: var(--blue-100);
    color: var(--blue-700);
    border-radius: var(--radius-pill);
    font-size: var(--text-eyebrow);
    min-width: 20px;
    block-size: 20px;
    padding-inline: var(--space-2);
    padding-block: 0;
    flex: none;
    font-variant-numeric: tabular-nums;
    vertical-align: middle
  }

  :global(html.tik-dark) .tbadge {
    background: var(--blue-900);
    color: var(--blue-200)
  }

  mark {
    background: var(--mark-bg);
    color: var(--mark-fg);
    border-radius: var(--radius-xs);
    padding: 0 1px
  }

  .star {
    color: var(--warning-500);
    display: grid
  }

  .rstar {
    display: grid;
    place-items: center;
    width: var(--control-xs);
    height: var(--control-xs);
    border-radius: var(--radius-control);
    color: var(--text-faint);
    flex: none
  }

  .rstar:hover {
    color: var(--text-body);
    background: var(--surface-sunken)
  }

  .rstar:active {
    background: var(--mail-pressed);
    color: var(--text-body)
  }

  .rstar.on {
    color: var(--warning-500)
  }

  .rstar.on :global(svg) {
    fill: currentColor
  }

  .clip {
    color: var(--text-faint);
    display: grid
  }

  .dot-unread {
    width: 9px;
    height: 9px;
    border-radius: var(--radius-pill);
    background: var(--blue-500)
  }

  .danger-h:hover {
    color: var(--danger-500)
  }

  .row {
    touch-action: pan-y
  }

  .row {
    will-change: auto
  }

  .mid > * {
    min-width: 0
  }

  .from {
    min-width: 0;
    overflow: hidden
  }

  @media (hover:hover) and (pointer:fine) {
  .side {
      min-inline-size: 0
    }
  }

  @media (hover:hover) and (pointer:fine) {
  .rowact {
      display: none;
      min-inline-size: 0
    }
  }

  @media (hover:hover) and (pointer:fine) {
  .row:hover .side,
  .row:focus-within .side {
      display: grid
    }
  }

  @media (hover:hover) and (pointer:fine) {
  .row:hover .rowact,
  .row:focus-within .rowact {
      display: flex;
      visibility: visible;
      position: absolute;
      inset-block: 0;
      inset-inline-end: var(--space-4);
      align-items: center;
      background: inherit;
      z-index: 1;
      padding-inline-start: var(--space-3)
    }
  }
  /* @css list/Row end */
</style>
